import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormBaseDropdown,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import { useFilterCount } from "@/shared/hooks/useFilterCount";
import { getContentKey, getPathCopy } from "@/domains/certifications/utils";
import { STATUS_OPTIONS } from "@/features/certifications/participants/constants";
import {
  ParticipantFiltersProps,
  ParticipantFiltersState,
  ParticipantStatusOption,
} from "@/features/certifications/participants/types";

const FILTER_NAMES = ["search", "track", "status"];
const UNCOUNTED_FILTERS = ["search"];
const DEFAULT_FILTERS: ParticipantFiltersState = {
  search: undefined,
  track: undefined,
  status: undefined,
};

const participantFiltersSchema: yup.ObjectSchema<ParticipantFiltersState> = yup
  .object({
    search: yup
      .string()
      .transform((value) => (value === "" ? undefined : value)),
    track: yup.number(),
    status: yup.mixed<ParticipantStatusOption>().oneOf(STATUS_OPTIONS),
  })
  .required();

const ParticipantFilters = ({
  certification,
  initialFilters,
  setFilters,
}: ParticipantFiltersProps) => {
  const { t } = useTranslation();

  const form = useForm<ParticipantFiltersState>({
    resolver: yupResolver(participantFiltersSchema),
    mode: "onChange",
    defaultValues: { ...DEFAULT_FILTERS, ...initialFilters },
  });

  const trackOptions = useMemo(() => {
    const contentKey = getContentKey(certification.type);
    return [...certification.tracks]
      .sort((a, b) => a.order - b.order)
      .map((track) => ({
        value: track.id,
        label: getPathCopy(t, contentKey, track).shortTitle,
      }));
  }, [certification, t]);

  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map((value) => ({
        value,
        label: t(`certification.admin.participants.statusFilter.${value}`),
      })),
    [t],
  );

  const filterFields: Record<string, FilterField> = useMemo(
    () => ({
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            suffix={<SearchOutlined />}
            placeholder={t(
              "certification.admin.participants.searchPlaceholder",
            )}
          />
        ),
      },
      track: {
        name: "track",
        label: t("certification.admin.participants.path"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="track"
            control={form.control}
            options={trackOptions}
            placeholder={t("certification.admin.participants.selectPath")}
            allowClear
          />
        ),
      },
      status: {
        name: "status",
        label: t("certification.admin.participants.status"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormBaseDropdown
            name="status"
            control={form.control}
            options={statusOptions}
            placeholder={t("certification.admin.participants.selectStatus")}
            allowClear
          />
        ),
      },
    }),
    [form.control, t, trackOptions, statusOptions],
  );

  const activeFilterCount = useFilterCount<ParticipantFiltersState>(
    form,
    undefined,
    UNCOUNTED_FILTERS,
  );

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={FILTER_NAMES}
      popoverTitle={t("certification.admin.participants.filters")}
      handleClear={() => {
        form.reset(DEFAULT_FILTERS);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      activeFilterCount={activeFilterCount}
      handleSearch={() => setFilters(form.getValues())}
      className="filter-layout"
    />
  );
};

export default ParticipantFilters;
